import { AuditAction, AuditLogDto } from '_lib/api';
import { FluentVariable } from '@fluent/bundle';
import { Theme } from '@material-ui/core';

export interface MetadataSummary {
  id: string;
  args?: Record<string, FluentVariable>;
}

// Palette rather than literals so both the light and the dark theme stay readable
export const actionColor = (theme: Theme, action: AuditAction): string => {
  switch (action) {
    case AuditAction.CREATE:
    case AuditAction.ENABLE:
      return theme.palette.success.main;
    case AuditAction.UPDATE:
      return theme.palette.primary.main;
    case AuditAction.DELETE:
      return theme.palette.error.main;
    case AuditAction.DISABLE:
      return theme.palette.warning.main;
    default:
      return theme.palette.text.secondary;
  }
};

export const actionLabelKey = (action: AuditAction): string => `audit-action-${action}`;

export const auditActor = (entry: AuditLogDto): string | null =>
  entry.username ?? entry.userEmail ?? null;

/*
 * The backend stores no before/after diff. `metadata` defaults to `{ method, path }`, which
 * is identical on every row and pure noise, so it is deliberately not summarised; only the
 * handlers that attach real context produce a line here.
 */
export const metadataSummary = (entry: AuditLogDto): MetadataSummary | null => {
  const { metadata } = entry;
  if (!metadata) {
    return null;
  }

  const { eventIds } = metadata;
  if (Array.isArray(eventIds)) {
    return {
      id: metadata.disabled === false ? 'audit-meta-bulk-enable' : 'audit-meta-bulk-disable',
      args: { count: eventIds.length },
    };
  }

  if (typeof metadata.roadSegmentId === 'string') {
    return {
      id: metadata.disabled === false ? 'audit-meta-segment-enable' : 'audit-meta-segment-disable',
      args: { segment: metadata.roadSegmentId },
    };
  }

  if ('value' in metadata) {
    return { id: 'audit-meta-value', args: { value: String(metadata.value) } };
  }

  return null;
};

export const metadataRaw = (entry: AuditLogDto): string =>
  (entry.metadata ? JSON.stringify(entry.metadata) : '');
